import type {
  MarkConfig,
  TrackEventData,
  ConversionProperties,
  IdentifyTraits,
  ConsentStatus,
  JsonValue,
} from '../types';
import type { MarkDependencies } from './adapters';
import { DEFAULT_ENDPOINT } from './constants';
import { DeliveryQueue } from './DeliveryQueue';
import { normalizeVisitorId } from './visitorId';

type ResolvedConfig = MarkConfig & { endpoint: string };
const TRACK_RESERVED_FIELDS = new Set(['event_name', 'user_id', 'consent_state', 'source', 'is_conversion']);
const IDENTIFY_RESERVED_FIELDS = new Set([
  'user_id',
  'visitor_id',
  'click_id',
  'campaign_id',
  'query',
  'consent_state',
  'source',
]);

export class MarkCore {
  private config: ResolvedConfig;
  private consentRequirement: boolean | 'auto';
  private siteId?: string;
  private siteHost?: string;
  private readonly queue: DeliveryQueue;
  private readonly sessionTimeoutMs: number;
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly batchedEvents: Array<Record<string, unknown>> = [];
  private tcfCachedAllowed = false;

  constructor(config: MarkConfig, private readonly deps: MarkDependencies) {
    this.validateConfig(config);
    this.config = {
      endpoint: config.endpoint ?? DEFAULT_ENDPOINT,
      ...config,
      include_page_context: config.include_page_context ?? true,
    };
    this.consentRequirement = config.require_consent ?? false;
    this.siteId = config.site_id;
    this.siteHost = config.site_host;
    this.sessionTimeoutMs = config.session_timeout_ms ?? 30 * 60 * 1000;
    this.queue = new DeliveryQueue(this.deps.transport, {
      debug: this.config.debug,
      loadPersisted: () => (this.deps.storage.getOutbox?.() as any[]) ?? [],
      savePersisted: (items) => this.deps.storage.setOutbox?.(items as unknown[]),
      onError: (error, event) => this.config.on_error?.(error, event),
    });
    this.warnMisconfiguredSiteHost();
    this.subscribeTcf();
  }

  /**
   * Best-effort synchronous drain that dispatches all queued events via
   * fetch keepalive. Intended for use on page unload (visibilitychange=hidden,
   * pagehide) where async fetch may be cancelled by the browser.
   */
  public drainViaBeacon() {
    this.flushBatch();
    this.queue.drainViaBeacon();
  }

  /**
   * Kicks the queue to retry any pending items now. Safe to call repeatedly;
   * used by the browser wrapper in response to `online` events or periodic
   * timers.
   */
  public kickQueue() {
    void this.queue.flush();
  }

  public track(eventName: string, data: TrackEventData = {}) {
    return this.trackInternal(eventName, data, false);
  }

  private trackInternal(
    eventName: string,
    data: TrackEventData = {},
    isConversion = false,
    options?: { preferBeacon?: boolean }
  ) {
    if (!eventName) {
      if (this.config.debug) {
        console.warn('[Mark] track called without event name');
      }
      return false;
    }

    if (!this.hasConsent() || this.isDntBlocked()) {
      if (this.config.debug) {
        console.warn('[Mark] Tracking blocked due to consent requirement.');
      }
      return false;
    }

    if (!isConversion && !this.shouldSampleTrack()) {
      return true;
    }

    const sanitizedData = this.sanitizeTrackData(data);
    const restData: TrackEventData = { ...sanitizedData };
    if ('query' in restData) {
      delete restData.query;
    }
    if ('site_id' in restData) {
      delete restData.site_id;
    }
    if ('site_host' in restData) {
      delete restData.site_host;
    }
    const payload: Record<string, unknown> = {
      event_name: eventName,
      message_id: this.createMessageId(),
      ...this.getIdentityFields(sanitizedData),
      ...restData,
    };
    if (isConversion) {
      payload.is_conversion = true;
    }

    // Add site_id and site_host if configured (event-level overrides init-level)
    const siteId = sanitizedData.site_id ?? this.siteId;
    const siteHost = sanitizedData.site_host ?? this.siteHost;

    if (siteId) {
      payload.site_id = siteId;
    }
    if (siteHost) {
      payload.site_host = siteHost;
    }

    if (this.config.include_page_context && typeof window !== 'undefined') {
      this.applyPageContext(payload);
      // If site_host wasn't explicitly set, use the host from page context
      if (!siteHost && payload.site) {
        payload.site_host = payload.site;
      }
    }

    this.applyInternalFlag(payload, sanitizedData.is_internal);

    const transformed = this.config.before_send ? this.config.before_send(payload) : payload;
    if (!transformed) {
      return true;
    }

    this.ensureSession();
    this.applySessionFields(transformed);
    if (this.config.batching?.enabled && !isConversion && !options?.preferBeacon) {
      this.enqueueBatch(transformed);
      return true;
    }
    this.queue.enqueue('/event', { ...transformed, __prefer_beacon: options?.preferBeacon === true });
    return true;
  }

  public identify(userId: string, traits: IdentifyTraits = {}) {
    if (!userId) {
      if (this.config.debug) {
        console.warn('[Mark] identify called without userId');
      }
      return;
    }

    if (!this.hasConsent() || this.isDntBlocked()) {
      if (this.config.debug) {
        console.warn('[Mark] Identify blocked due to consent requirement.');
      }
      return;
    }

    this.deps.storage.update({ user_id: userId });

    const payload: Record<string, unknown> = {
      user_id: userId,
      message_id: this.createMessageId(),
      ...this.sanitizeIdentifyTraits(traits),
      ...this.getIdentityFields(),
    };

    // Add site_id and site_host if configured
    if (this.siteId) {
      payload.site_id = this.siteId;
    }
    if (this.siteHost) {
      payload.site_host = this.siteHost;
    }

    this.applyInternalFlag(payload);

    const transformed = this.config.before_send ? this.config.before_send(payload) : payload;
    if (!transformed) return;

    this.ensureSession();
    this.applySessionFields(transformed);
    this.queue.enqueue('/identify', transformed);
  }

  public conversion(eventName: string, data: ConversionProperties = {}) {
    return this.trackInternal(eventName, data, true);
  }

  public trackWithOptions(eventName: string, data: TrackEventData = {}, options?: { preferBeacon?: boolean }) {
    return this.trackInternal(eventName, data, false, options);
  }

  /**
   * Returns the current visitor ID from storage, if any.
   * Used by browser/Node wrappers to expose a stable pseudonymous ID for server-side attribution.
   */
  public getVisitorId(): string | undefined {
    return this.deps.storage.getVisitorId();
  }

  /**
   * Adopts or replaces the stored visitor id with a trusted first-party value.
   * Use when an external anonymous id (for example a platform client id) arrives
   * after init. Returns false when the id is invalid.
   */
  public setVisitorId(visitorId: string): boolean {
    const normalized = normalizeVisitorId(visitorId);
    if (!normalized) {
      if (this.config.debug) {
        console.warn('[Mark] setVisitorId called with an invalid visitor id.');
      }
      return false;
    }
    if (this.deps.storage.setVisitorId) {
      return this.deps.storage.setVisitorId(normalized);
    }
    if (this.deps.storage.getVisitorId() === normalized) {
      return true;
    }
    this.deps.storage.update({ visitor_id: normalized });
    return true;
  }

  public setConsent(status: ConsentStatus) {
    const previous = this.deps.storage.getConsentStatus();
    this.deps.storage.setConsentStatus(status);
    if (status === 'denied') {
      this.deps.storage.clearAttribution?.();
      this.deps.storage.clearCookieVisitorId?.();
      this.deps.storage.setInternal?.(false);
    } else if (
      status === 'granted' &&
      previous === 'denied' &&
      this.config.rotate_visitor_on_consent_change
    ) {
      this.deps.storage.rotateVisitorId?.();
    }
    const payload: Record<string, unknown> = {
      visitor_id: this.deps.storage.getVisitorId(),
      consent_state: status,
      source: 'sdk',
      message_id: this.createMessageId(),
    };

    // Add site_id and site_host if configured
    if (this.siteId) {
      payload.site_id = this.siteId;
    }
    if (this.siteHost) {
      payload.site_host = this.siteHost;
    }

    this.applyInternalFlag(payload);

    const transformed = this.config.before_send ? this.config.before_send(payload) : payload;
    if (!transformed) return;
    this.queue.enqueue('/consent', transformed);
  }

  public reset() {
    this.deps.storage.update({
      user_id: undefined,
      last_click_id: undefined,
      campaign_id: undefined,
      query_params: undefined,
      session_id: undefined,
      session_started_at: undefined,
      last_activity_at: undefined,
      is_internal: undefined,
    });
    this.deps.storage.rotateVisitorId?.();
  }

  /**
   * Marks or unmarks the current visitor as internal traffic. While set, every
   * subsequent event (track/identify/conversion/consent) is stamped with
   * `is_internal: true`, so the backend can exclude it from customer-facing
   * reports by default.
   *
   * The flag is persisted via the storage adapter (browser: localStorage) so
   * it survives reloads, and is cleared by `reset()` and by
   * `setConsent('denied')`.
   */
  public setInternal(value: boolean) {
    this.deps.storage.setInternal?.(Boolean(value));
  }

  /**
   * Returns the currently persisted internal-traffic flag, if any.
   */
  public getInternal(): boolean {
    return Boolean(this.deps.storage.getInternal?.());
  }

  public flush() {
    this.flushBatch();
    return this.queue.flush();
  }

  public getStats() {
    return this.queue.getStats();
  }

  /**
   * Stamps `is_internal: true` on the payload when either:
   * - the persistent visitor flag is set (via setInternal), or
   * - the caller passed `is_internal: true` on this specific event.
   *
   * Explicit `is_internal: false` on a single event wins over the visitor flag
   * so individual calls can opt out.
   */
  private applyInternalFlag(payload: Record<string, unknown>, eventValue?: boolean) {
    if (eventValue === false) {
      delete payload.is_internal;
      return;
    }
    const stored = this.deps.storage.getInternal?.() === true;
    if (eventValue === true || stored) {
      payload.is_internal = true;
    } else {
      delete payload.is_internal;
    }
  }

  private getIdentityFields(data?: TrackEventData) {
    const visitorId = data?.visitor_id ?? this.deps.storage.getVisitorId();
    const userId = data?.user_id ?? this.deps.storage.getUserId?.();
    const clickId = data?.click_id ?? this.deps.storage.getLastClickId();
    const campaignId = data?.campaign_id ?? this.deps.storage.getCampaignId();
    const sessionId = data?.session_id ?? this.deps.storage.getSessionId?.();

    // Auto-capture query params from storage or event data
    const storedQuery = this.deps.storage.getQueryParams() ?? {};
    const inputQuery = (data?.query as Record<string, string> | undefined) ?? {};

    // Merge: stored values serve as defaults, input values override/append
    const query = { ...storedQuery, ...inputQuery };

    const identity: Record<string, unknown> = {};

    if (visitorId) identity.visitor_id = visitorId;
    if (userId) identity.user_id = userId;
    if (clickId) identity.click_id = clickId;
    if (campaignId) identity.campaign_id = campaignId;
    if (sessionId) identity.session_id = sessionId;

    const sessionStartedAt = this.deps.storage.getSessionStartedAt?.();
    if (sessionId && sessionStartedAt) {
      identity.session_started_at = sessionStartedAt;
      identity.session_elapsed_ms = Date.now() - Date.parse(sessionStartedAt);
    }

    if (Object.keys(query).length > 0) {
      identity.query = query;
    }

    return identity;
  }

  /**
   * Patches session fields after ensureSession when the first event in a session was built
   * before rotation created an id. Rotation events keep the previous session_id from identity.
   */
  private applySessionFields(payload: Record<string, unknown>) {
    if (!payload.session_id) {
      const sessionId = this.deps.storage.getSessionId?.();
      const sessionStartedAt = this.deps.storage.getSessionStartedAt?.();
      if (sessionId) payload.session_id = sessionId;
      if (sessionStartedAt) payload.session_started_at = sessionStartedAt;
    }
    const startedAt = payload.session_started_at;
    if (payload.session_id && typeof startedAt === 'string') {
      payload.session_elapsed_ms = Date.now() - Date.parse(startedAt);
    }
  }

  private hasConsent() {
    if (this.config.consent_source?.type === 'tcf' && typeof window !== 'undefined') {
      if (!this.tcfCachedAllowed) {
        return false;
      }
    }
    if (!this.consentRequirement) {
      return true;
    }

    const stored = this.deps.storage.getConsentStatus();
    const state = stored;

    if (this.consentRequirement === 'auto') {
      // In auto mode, default to deny until consent is explicitly granted.
      return state === 'granted';
    }

    return state === 'granted';
  }

  private sanitizeTrackData(data: TrackEventData) {
    const sanitized: Record<string, JsonValue | undefined> = {};
    for (const [key, value] of Object.entries(data)) {
      if (TRACK_RESERVED_FIELDS.has(key)) {
        continue;
      }
      sanitized[key] = value;
    }
    return sanitized as TrackEventData;
  }

  private sanitizeIdentifyTraits(traits: IdentifyTraits) {
    const sanitized: Record<string, JsonValue | undefined> = {};
    for (const [key, value] of Object.entries(traits)) {
      if (IDENTIFY_RESERVED_FIELDS.has(key)) {
        continue;
      }
      sanitized[key] = value;
    }
    return sanitized as IdentifyTraits;
  }

  private validateConfig(config: MarkConfig) {
    if (!config.key || !config.key.trim()) {
      throw new Error('[Mark] `key` must be a non-empty string.');
    }
    if (config.endpoint) {
      try {
        // eslint-disable-next-line no-new
        new URL(config.endpoint);
      } catch {
        throw new Error('[Mark] `endpoint` must be a valid absolute URL.');
      }
    }
    if (typeof config.site_id === 'string' && !config.site_id.trim()) {
      throw new Error('[Mark] `site_id` cannot be an empty string.');
    }
    if (typeof config.site_host === 'string' && !config.site_host.trim()) {
      throw new Error('[Mark] `site_host` cannot be an empty string.');
    }
    if (config.visitor_id !== undefined && !normalizeVisitorId(config.visitor_id)) {
      throw new Error('[Mark] `visitor_id` must be a non-empty string (max 256 characters).');
    }
  }

  private applyPageContext(payload: Record<string, unknown>) {
    if (typeof document === 'undefined') {
      return;
    }
    if (!payload.site) payload.site = window.location.host;
    if (!payload.page) payload.page = window.location.pathname;
    if (!payload.title) payload.title = document.title;
    if (!payload.referrer && document.referrer) {
      payload.referrer = this.scrubReferrer(document.referrer);
    }
    // full URL omitted for privacy; include only if explicitly provided by the caller
  }

  private enqueueBatch(event: Record<string, unknown>) {
    this.batchedEvents.push(event);
    const maxSize = this.config.batching?.max_size ?? 20;
    if (this.batchedEvents.length >= maxSize) {
      this.flushBatch();
      return;
    }
    if (!this.batchTimer) {
      const intervalMs = this.config.batching?.flush_interval_ms ?? 2000;
      this.batchTimer = setTimeout(() => {
        this.batchTimer = null;
        this.flushBatch();
      }, intervalMs);
    }
  }

  private flushBatch() {
    if (this.batchedEvents.length === 0) return;
    const path = this.config.batching?.endpoint_path ?? '/events';
    const payload = this.batchedEvents.splice(0, this.batchedEvents.length);
    this.queue.enqueue(path, { events: payload, message_id: this.createMessageId() });
  }

  private createMessageId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `msg_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  private ensureSession() {
    const now = Date.now();
    const currentId = this.deps.storage.getSessionId?.();
    const lastActivityIso = this.deps.storage.getLastActivityAt?.();
    const lastActivity = lastActivityIso ? Date.parse(lastActivityIso) : 0;
    const shouldRotate =
      !currentId ||
      !lastActivity ||
      now - lastActivity >= this.sessionTimeoutMs ||
      this.crossedUtcDay(lastActivity, now);
    if (shouldRotate) {
      const newSessionId = this.createMessageId();
      const nowIso = new Date(now).toISOString();
      this.deps.storage.update({
        session_id: newSessionId,
        session_started_at: nowIso,
        last_activity_at: nowIso,
      });
      return;
    }
    this.deps.storage.update({ last_activity_at: new Date(now).toISOString() });
  }

  private crossedUtcDay(previousTs: number, currentTs: number) {
    const prev = new Date(previousTs);
    const next = new Date(currentTs);
    return (
      prev.getUTCFullYear() !== next.getUTCFullYear() ||
      prev.getUTCMonth() !== next.getUTCMonth() ||
      prev.getUTCDate() !== next.getUTCDate()
    );
  }

  private shouldSampleTrack() {
    if (typeof this.config.sample_rate !== 'number') return true;
    if (this.config.sample_rate <= 0) return false;
    if (this.config.sample_rate >= 1) return true;
    return Math.random() <= this.config.sample_rate;
  }

  private isDntBlocked() {
    if (!this.config.honor_dnt || typeof navigator === 'undefined') {
      return false;
    }
    const dnt = navigator.doNotTrack;
    const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
    return dnt === '1' || gpc === true;
  }

  private scrubReferrer(referrer: string) {
    try {
      const refUrl = new URL(referrer);
      if (typeof window === 'undefined') return referrer;
      const currentUrl = new URL(window.location.href);
      if (refUrl.origin !== currentUrl.origin) {
        refUrl.search = '';
        return refUrl.toString();
      }
      return referrer;
    } catch {
      return referrer;
    }
  }

  private warnMisconfiguredSiteHost() {
    if (!this.config.debug || !this.siteHost || typeof window === 'undefined') {
      return;
    }
    if (window.location.host !== this.siteHost) {
      console.warn('[Mark] config.site_host does not match current host', {
        expected: this.siteHost,
        actual: window.location.host,
      });
    }
    if (this.siteId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(this.siteId)) {
      console.warn('[Mark] config.site_id does not look like UUID v4', this.siteId);
    }
  }

  /**
   * Subscribes to the IAB TCF v2 CMP via `__tcfapi('addEventListener', ...)`.
   * Result is cached in `tcfCachedAllowed` so the synchronous `hasConsent()`
   * path does not need to await the CMP. If the CMP is not yet present, we
   * poll briefly (CMPs commonly load asynchronously) and give up after ~2s.
   */
  private subscribeTcf() {
    const source = this.config.consent_source;
    if (source?.type !== 'tcf' || typeof window === 'undefined') return;
    const purposes = source.purposes;

    const attempt = (retries: number) => {
      try {
        const w = window as Window & {
          __tcfapi?: (
            command: string,
            version: number,
            callback: (
              tcData?: {
                eventStatus?: string;
                gdprApplies?: boolean;
                listenerId?: number;
                purpose?: { consents?: Record<string, boolean> };
              },
              ok?: boolean
            ) => void,
            parameter?: number
          ) => void;
        };
        if (typeof w.__tcfapi !== 'function') {
          if (retries > 0) {
            setTimeout(() => attempt(retries - 1), 200);
          }
          return;
        }
        w.__tcfapi('addEventListener', 2, (tcData, ok) => {
          if (!ok || !tcData) {
            this.tcfCachedAllowed = false;
            return;
          }
          if (tcData.gdprApplies === false) {
            this.tcfCachedAllowed = true;
            return;
          }
          const consents = tcData.purpose?.consents ?? {};
          this.tcfCachedAllowed = purposes.every((p) => consents[String(p)] === true);
        });
      } catch {
        this.tcfCachedAllowed = false;
      }
    };

    attempt(10);
  }
}

